import {
  Injectable,
  WritableSignal,
  Signal,
  computed,
  signal,
  inject,
} from '@angular/core';
import { ApiService } from '../services/api-services/api.service';
import {
  CalendarEvent,
  DATETIMEFORMART,
  EventRequest,
  ForwardEventRequest,
} from '../interfaces/interfaces';
import { catchError, firstValueFrom, of, tap } from 'rxjs';
import * as moment from 'moment';
import { UserStore } from './user.store';
import { FloorplanStore } from './floorplan.store';
import { PlacesStore } from './places.store';
import { HapticsService } from '../services/haptics-service/haptics.service';
import { AvatarService } from '../services/avatarService/avatar.service';
import { mergeIncomingCheckoutMetadata } from '../utils/checkout-delegates';

interface BookingsSortedByDate {
  [key: string]: CalendarEvent[];
}

@Injectable({
  providedIn: 'root',
})
export class BookingsStore {
  private _allBookingsSignal: WritableSignal<CalendarEvent[]> = signal<
    CalendarEvent[]
  >([]);
  private _bookingsTypeFilterSignal: WritableSignal<
    'all' | 'without-resource' | 'with-resource'
  > = signal<'all' | 'without-resource' | 'with-resource'>('all');
  private _temporaryVisibility = signal<{
    showActive: boolean;
    showExpired: boolean;
  } | null>(null);
  private _loadingSignal: WritableSignal<boolean> = signal<boolean>(false);
  private _errorSignal: WritableSignal<string | null> = signal<string | null>(
    null,
  );

  //Computed signals
  public allBookingsRaw: Signal<CalendarEvent[]> =
    this._allBookingsSignal.asReadonly();
  public allBookings: Signal<CalendarEvent[]> = computed(() =>
    this.mapAndFilterBookings(this._allBookingsSignal()),
  );

  public myBookings: Signal<BookingsSortedByDate> = computed(() =>
    this.sortBookingsByDate(
      this.filterBookingsByTypeAndSegment(
        this.allBookings(),
        'mine',
        this.bookingsTypeFilter(),
      ),
    ),
  );

  public myInvitations: Signal<BookingsSortedByDate> = computed(() =>
    this.sortBookingsByDate(
      this.filterBookingsByTypeAndSegment(
        this.allBookings(),
        'invitations',
        this.bookingsTypeFilter(),
      ),
    ),
  );
  public pendingInvitationsCount: Signal<number> = computed(() =>
    this.getPendingInvitationsCount(
      this.myInvitations(),
      this.userStore.user()?.mail,
    ),
  );

  public bookingsTypeFilter: Signal<
    'all' | 'without-resource' | 'with-resource'
  > = this._bookingsTypeFilterSignal.asReadonly();

  public showExpiredFilter: Signal<boolean> = computed(() => {
    const temporary = this._temporaryVisibility();
    if (temporary) {
      return temporary.showExpired;
    }
    return (
      this.userStore.userPreferences()?.userSettings?.showExpiredBookings !==
      false
    );
  });
  public showActiveFilter: Signal<boolean> = computed(() => {
    const temporary = this._temporaryVisibility();
    if (temporary) {
      return temporary.showActive;
    }
    return (
      this.userStore.userPreferences()?.userSettings?.showActiveBookings !==
      false
    );
  });
  public allBookingsFull: Signal<CalendarEvent[]> =
    this._allBookingsSignal.asReadonly();

  public loading: Signal<boolean> = this._loadingSignal.asReadonly();
  public error: Signal<string | null> = this._errorSignal.asReadonly();

  //Constructor
  private apiService: ApiService = inject(ApiService);
  private userStore: UserStore = inject(UserStore);
  private floorplanStore: FloorplanStore = inject(FloorplanStore);
  private placesStore: PlacesStore = inject(PlacesStore);
  private hapticsService: HapticsService = inject(HapticsService);
  private avatarService: AvatarService = inject(AvatarService);

  //   Actions
  public setBookingsTypeFilter(
    bookingsType: 'all' | 'without-resource' | 'with-resource',
  ) {
    this._bookingsTypeFilterSignal.set(bookingsType);
  }

  public temporaryVisibility(): {
    showActive: boolean;
    showExpired: boolean;
  } | null {
    return this._temporaryVisibility();
  }

  public setTemporaryVisibility(
    visibility: { showActive: boolean; showExpired: boolean } | null,
  ) {
    this._temporaryVisibility.set(visibility);
  }

  public resetStoreData() {
    this._allBookingsSignal.set([]);
    this._bookingsTypeFilterSignal.set('all');
    this._temporaryVisibility.set(null);
    this._loadingSignal.set(false);
    this._errorSignal.set(null);
  }

  // API calls
  public getMyBookings() {
    this._allBookingsSignal.set([]);
    this._loadingSignal.set(true);
    this._errorSignal.set(null);
    return firstValueFrom(
      this.apiService.getBookingsFromMyCalendarManager().pipe(
        tap((bookings: CalendarEvent[]) => {
          this.mapBookingsToFloorplans(bookings);
          this.mapBookingsToAvatars(bookings);
          this.updateTimeForAllDayBookingsUTC(bookings);
          this._allBookingsSignal.set(bookings);
          this._loadingSignal.set(false);
        }),
        catchError((error) => {
          this._errorSignal.set(error.message || 'Failed to fetch bookings');
          this._loadingSignal.set(false);
          return of([]);
        }),
      ),
    );
  }

  public makeNewBooking(eventRequest: any) {
    return this.apiService
      .createCalendarEventNew(eventRequest, eventRequest.isOnPrem)
      .pipe(
        tap((res) => {
          // Handle success
          console.log('Event created successfully:', res);
          // Set place as pending
          this.placesStore.setPlacePending(eventRequest.placeEmailAddress);
          this.addBookingLocal(res);
          // Trigger haptic feedback
          this.hapticsService.lightImpact();
        }),
        catchError((ex) => {
          console.log('Error creating event:', ex);
          // Trigger haptic feedback even on error
          this.hapticsService.lightImpact();
          // Re-throw the error so the component can handle the specific error messages
          throw ex;
        }),
      );
  }

  public acceptInvitation(id: string, isSeries: boolean = false) {
    this.setMeetingsLoading(id, isSeries);
    return this.apiService.InvitationRespond(id, 'Accept').pipe(
      tap((res) => {
        this.updateBookingLocal(res, isSeries);
      }),
      catchError((ex) => {
        console.log('Error accepting invitation:', ex);
        this.setMeetingsLoading(id, isSeries, false);
        // Trigger haptic feedback even on error
        this.hapticsService.lightImpact();
        // Re-throw the error so the component can handle the specific error messages
        throw ex;
      }),
    );
  }

  public declineInvitation(id: string, isSeries: boolean = false) {
    this.setMeetingsLoading(id, isSeries);
    return this.apiService.InvitationRespond(id, 'Decline').pipe(
      tap((res) => {
        this.updateBookingLocal(res, isSeries);
      }),
      catchError((ex) => {
        console.log('Error declining invitation:', ex);
        this.setMeetingsLoading(id, isSeries, false);
        // Trigger haptic feedback even on error
        this.hapticsService.lightImpact();
        // Re-throw the error so the component can handle the specific error messages
        throw ex;
      }),
    );
  }

  public tentativelyAcceptInvitation(id: string, isSeries: boolean = false) {
    this.setMeetingsLoading(id, isSeries);
    return this.apiService.InvitationRespond(id, 'TentativelyAccept').pipe(
      tap((res) => {
        this.updateBookingLocal(res, isSeries);
      }),
      catchError((ex) => {
        console.log('Error tentatively accepting invitation:', ex);
        this.setMeetingsLoading(id, isSeries, false);
        // Trigger haptic feedback even on error
        this.hapticsService.lightImpact();
        // Re-throw the error so the component can handle the specific error messages
        throw ex;
      }),
    );
  }

  public tentativelyAcceptInvitationWithNewTime(
    id: string,
    isSeries: boolean = false,
    start: string,
    end: string,
  ) {
    this.setMeetingsLoading(id, isSeries);
    return this.apiService
      .invitationRespondWithNewTime(id, { start, end })
      .pipe(
        tap((res) => {
          this.updateBookingLocal(res, isSeries);
        }),
        catchError((ex) => {
          console.log(
            'Error tentatively accepting invitation with new time:',
            ex,
          );
          this.setMeetingsLoading(id, isSeries, false);
          // Trigger haptic feedback even on error
          this.hapticsService.lightImpact();
          // Re-throw the error so the component can handle the specific error messages
          throw ex;
        }),
      );
  }

  public deleteBooking(id: string, isSeries: boolean = false) {
    this.setMeetingsLoading(id, isSeries);
    return this.apiService.deleteCalendarEvent(id).pipe(
      tap((res) => {
        if (res) {
          const fakeEvent = {
            id: !isSeries ? id : '',
            seriesMasterId: isSeries ? id : '',
          };
          this.deleteBookingLocal(fakeEvent as CalendarEvent);
        }
      }),
      catchError((ex) => {
        console.log('Error creating event:', ex);

        this.setMeetingsLoading(id, isSeries, false);
        // Trigger haptic feedback even on error
        this.hapticsService.lightImpact();
        // Re-throw the error so the component can handle the specific error messages
        throw ex;
      }),
    );
  }

  // create helper function to uodate booking in datebase in backend
  // ask about moving and refactoring the me into the calendarManager
  //
  public editBooking(
    id: string,
    isSeries: boolean,
    eventRequest: EventRequest,
  ) {
    this.setMeetingsLoading(id, isSeries, true);
    return this.apiService.editCalendarEvent(id, eventRequest).pipe(
      tap((res) => {
        this.updateBookingLocal(res, isSeries);
      }),
      catchError((ex) => {
        console.log('Error editing event:', ex);

        this.setMeetingsLoading(id, isSeries, false);
        // Trigger haptic feedback even on error
        this.hapticsService.lightImpact();
        // Re-throw the error so the component can handle the specific error messages
        throw ex;
      }),
    );
  }
  public forwardBooking(
    forwardRequest: ForwardEventRequest,
    forwardType: string,
  ) {
    this.setMeetingsLoading(forwardRequest.eventId, false, true);
    return this.apiService.forwardEvent(forwardRequest, forwardType).pipe(
      tap((res) => {
        console.log(res);

        this.updateBookingLocal(res, false);
        this.setMeetingsLoading(forwardRequest.eventId, false, false);
      }),
      catchError((ex) => {
        console.log('Error forwarding event:', ex);

        // Trigger haptic feedback even on error
        this.hapticsService.lightImpact();
        // Re-throw the error so the component can handle the specific error messages
        throw ex;
      }),
    );
  }

  // Websocket events callbacks

  public deleteBookingLocal(event: CalendarEvent) {
    this._allBookingsSignal.update((bookings) =>
      bookings.filter((booking) =>
        event.seriesMasterId
          ? booking.seriesMasterId != event.seriesMasterId
          : booking.id != event.id,
      ),
    );
  }

  public addBookingLocal(event: CalendarEvent) {
    if (event.place != undefined) {
      event.place = this.placesStore.getPlaceByEmailAddress(
        event.place.emailAddress,
      );
    }

    //temp fix for series events to exclude the series master event from being added to the store.
    // The series master event is not needed in the store as it is not a real event and will be filtered out when displaying the events in the UI.
    if(event.recurrencePattern != null &&  event.seriesMasterId == '') {
      this._allBookingsSignal.update((bookings) =>
        bookings.filter((booking) => booking.id !== event.id),
      );
      return;
    }
    //the addBookingLocal is being called from webSocket on (create callBack) and upon event creation in Sparo
    // to avoid duplicate events in the store we check if the event already exists in the store.
    if (this.allBookings().find((booking) => booking.id === event.id)) {
      const isSeries = event.seriesMasterId ? true : false;
      this.updateBookingLocal(event, isSeries);
    } else {
      event.isNewEvent = true;
      this.updateTimeForAllDayBookingsUTC([event]);
      this._allBookingsSignal.update((bookings) => [...bookings, event]);
    }
  }

  public updateBookingLocal(
    event: CalendarEvent | CalendarEvent[],
    isSeries: boolean = false,
  ) {
    if (isSeries && Array.isArray(event) && event.length > 0) {
      // WE DID THIS TEMPORARILY TO FIX THE BUG FOR EDITING ALL SERIES ( KEEP THE ARRAY CONDITION )
      event = event as CalendarEvent[];
      const updatedEventsMap = new Map<string, CalendarEvent>();
      this.updateTimeForAllDayBookingsUTC(event);
      event.forEach((e) => {
        if (e.place != undefined) {
          e.place = this.placesStore.getPlaceByEmailAddress(
            e.place.emailAddress,
          );
        }
        const existingEvent = this._allBookingsSignal().find(
          (booking) => booking.id === e.id,
        );
        updatedEventsMap.set(e.id, {
          ...e,
          attendees: mergeIncomingCheckoutMetadata(
            existingEvent?.attendees,
            e.attendees,
          ),
        });
      });

      this._allBookingsSignal.update((bookings) =>
        bookings.map((booking) =>
          updatedEventsMap.has(booking.id)
            ? updatedEventsMap.get(booking.id)
            : booking,
        ),
      );
    } else {
      event = event as CalendarEvent;
      if (this.isSeriesMaster(event)) {
        return;
      }
      if (event.place != undefined) {
        event.place = this.placesStore.getPlaceByEmailAddress(
          event.place.emailAddress,
        );
      }
      const existingEvent = this._allBookingsSignal().find(
        (booking) => booking.id === (event as CalendarEvent).id,
      );
      event = {
        ...event,
        attendees: mergeIncomingCheckoutMetadata(
          existingEvent?.attendees,
          event.attendees,
        ),
      };
      this.updateTimeForAllDayBookingsUTC([event]);
      this._allBookingsSignal.update((bookings) =>
        bookings.map((booking) =>
          booking.id === (event as CalendarEvent).id
            ? { ...(event as CalendarEvent), isNewEvent: true }
            : booking,
        ),
      );
    }
  }

  private isSeriesMaster(event: CalendarEvent): boolean {
    return (
      !!event?.id &&
      (event.type?.toLowerCase() === 'seriesmaster' ||
        (!!event.recurrencePattern && !event.seriesMasterId))
    );
  }

  public checkInBookingLocal(event: CalendarEvent) {
    if (moment(event.startTime).isBefore(moment().utc())) {
      event.checkedIn = true;
      this.updateBookingLocal(event);
    }
  }
  //   Helpers functions

  public mapAndFilterBookings(bookings: CalendarEvent[]) {
    bookings.forEach((booking) => {
      this.isMeetingExpired(booking);
      this.isMeetingActive(booking);
    });

    return bookings.filter((booking) => {
      if (!this.showExpiredFilter() && booking.isExpired) {
        return false;
      }
      if (!this.showActiveFilter() && booking.isActive) {
        return false;
      }
      return true;
    });
  }

  private isMeetingExpired(booking: CalendarEvent) {
    booking.isExpired = booking.endTime < new Date().toISOString();
    return booking.isExpired;
  }

  private isMeetingActive(booking: CalendarEvent) {
    const now = new Date().toISOString();
    booking.isActive = booking.startTime <= now && booking.endTime > now;
    return booking.isActive;
  }

  public mapBookingsToFloorplans(bookings: CalendarEvent[]) {
    try {
      const floorplans = this.floorplanStore.allFloorplansList();
      if (!floorplans || floorplans.length === 0) {
        console.warn('No floorplans available for mapping');
        return;
      }

      bookings.forEach((booking) => {
        try {
          if (!booking.place || !booking.place.emailAddress) {
            return; // Skip this booking if place or emailAddress is missing
          }

          const matchingFloorplan = floorplans.find((floorplan) => {
            try {
              // Skip data URLs
              if (floorplan.floorMapImage.startsWith('data')) {
                return false;
              }

              // Parse JSON safely
              const parsedImage = JSON.parse(floorplan.floorMapImage);

              // Check if objects array exists and find matching object
              if (parsedImage.objects && Array.isArray(parsedImage.objects)) {
                return parsedImage.objects.some(
                  (object) => object.name === booking.place.emailAddress,
                );
              }

              return false;
            } catch (parseError) {
              console.warn('Failed to parse floorplan image JSON:', parseError);
              return false;
            }
          });

          booking.place.floorMapId = matchingFloorplan?._id;
        } catch (bookingError) {
          console.warn(
            'Error processing booking for floorplan mapping:',
            bookingError,
          );
        }
      });
    } catch (error) {
      console.error('Error in mapBookingsToFloorplans:', error);
    }
  }

  public mapBookingsToAvatars(bookings: CalendarEvent[]) {
    const avatars = this.avatarService.avatarList();
    bookings.forEach((booking) => {
      const avatar = avatars.find(
        (avatar) => avatar.upn == booking?.place?.emailAddress,
      );
      if (booking.place) {
        booking.place.avatar = avatar?.avatar;
      }
    });
  }

  private findBookingById(id: string, isSeries: boolean = false) {
    return this._allBookingsSignal().filter((booking) =>
      isSeries ? booking.seriesMasterId == id : booking.id == id,
    );
  }

  private setMeetingsLoading(
    id: string,
    isSeries: boolean = false,
    loading: boolean = true,
  ) {
    isSeries
      ? this._allBookingsSignal.update((bookings) =>
          bookings.map((booking) => {
            const userResponse = booking.attendees.find(
              (attendee) =>
                attendee?.userPrincipalName ==
                this.userStore.user()?.userPrincipalName,
            )?.response;
            const didUserNotRespond =
              userResponse == 'None' ||
              userResponse == 'NotResponded' ||
              userResponse == null ||
              userResponse == '';
            return booking.seriesMasterId == id && didUserNotRespond
              ? { ...booking, isLoading: loading }
              : booking;
          }),
        )
      : this._allBookingsSignal.update((bookings) =>
          bookings.map((booking) =>
            booking.id == id ? { ...booking, isLoading: loading } : booking,
          ),
        );
  }

  private getPendingInvitationsCount(
    bookings: BookingsSortedByDate,
    loggedInUserEmail: string,
  ) {
    return Object.values(bookings)
      .flat()
      .filter((event) => {
        if (event.isOrganizer == false && event.isCancelled == false) {
          let meAsAnAttendee = event.attendees.find((attendee) => {
            if (attendee)
              return attendee?.userPrincipalName == loggedInUserEmail;
          });
          if (
            meAsAnAttendee &&
            (meAsAnAttendee.response == 'None' ||
              meAsAnAttendee.response == 'NotResponded')
          )
            return true;
        }
      }).length;
  }

  private filterBookingsByTypeAndSegment(
    bookings: CalendarEvent[],
    segment: 'mine' | 'invitations',
    type: 'all' | 'without-resource' | 'with-resource' = 'all',
  ) {
    const filteredBookingsByType =
      type == 'all'
        ? bookings
        : type == 'without-resource'
          ? bookings.filter(
              (booking: CalendarEvent) => booking.isResourcelessEvent == true,
            )
          : bookings.filter(
              (booking: CalendarEvent) => booking.isResourcelessEvent == false,
            );

    if (segment === 'mine') {
      return filteredBookingsByType.filter((booking: CalendarEvent) => {
        if (booking.isOrganizer) {
          return true;
        }

        return this.hasUserRespondedToInvitation(booking);
      });
    }

    return filteredBookingsByType.filter(
      (booking: CalendarEvent) =>
        !booking.isOrganizer && this.isInvitationPendingResponse(booking),
    );
  }

  private hasUserRespondedToInvitation(booking: CalendarEvent) {
    const userResponse = this.getCurrentUserResponse(booking);
    return !this.isResponsePending(userResponse);
  }

  private isInvitationPendingResponse(booking: CalendarEvent) {
    const userResponse = this.getCurrentUserResponse(booking);
    return this.isResponsePending(userResponse);
  }

  private getCurrentUserResponse(booking: CalendarEvent) {
    const user = this.userStore.user();
    const userEmail = user?.mail?.toLowerCase();
    const userPrincipalName = user?.userPrincipalName?.toLowerCase();

    const attendee = booking.attendees?.find((bookingAttendee) => {
      const attendeePrincipalName =
        bookingAttendee?.userPrincipalName?.toLowerCase();
      return (
        attendeePrincipalName &&
        (attendeePrincipalName == userEmail ||
          attendeePrincipalName == userPrincipalName)
      );
    });

    return attendee?.response;
  }

  private isResponsePending(response?: string | null) {
    const normalizedResponse = response?.toLowerCase();
    return (
      normalizedResponse == null ||
      normalizedResponse == '' ||
      normalizedResponse == 'none' ||
      normalizedResponse == 'notresponded'
    );
  }

  private sortBookingsByDate(bookings: CalendarEvent[]) {
    // Extract all booking IDs to keep track of existing bookings
    const bookingsSortedByDate: BookingsSortedByDate = {};
    let bookingsIds = bookings.map((booking) => booking.id);
    // bookings = this.floorplanSearchService.mapBookingsListToItsFloorMap(bookings);
    bookings.forEach((booking) => {
      // Convert booking start time to a Date object and format it to YYYY-MM-DD
      const startDate = new Date(booking.startTime);
      const bookingDate = moment(startDate).format('YYYY-MM-DD');
      // If the date is not already a key in bookingsSortedByDate, initialize it with an empty array
      if (!bookingsSortedByDate[bookingDate]) {
        bookingsSortedByDate[bookingDate] = [];
      }

      // Check if the booking already exists in the sorted bookings for the current date
      const existingBookingIndex = bookingsSortedByDate[bookingDate].findIndex(
        (existingBooking) => existingBooking.id === booking.id,
      );
      // If the booking does not exist, add it to the sorted bookings for the current date
      if (existingBookingIndex == -1) {
        bookingsSortedByDate[bookingDate].push(booking);
        // Sort the bookings based on startTime inside the array of the found date
        bookingsSortedByDate[bookingDate].sort(
          (a, b) =>
            new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
        );
      } else {
        // If the booking exists, update it in the sorted bookings for the current date
        bookingsSortedByDate[bookingDate][existingBookingIndex] = booking;
      }
    });

    // Remove bookings that are no longer in the bookings array
    for (const date in bookingsSortedByDate) {
      bookingsSortedByDate[date].forEach((booking) => {
        // If the booking ID is not in the bookingsIds array, remove it from the sorted bookings
        if (!bookingsIds.includes(booking.id)) {
          bookingsSortedByDate[date].splice(
            bookingsSortedByDate[date].indexOf(booking),
            1,
          );
        }
      });
      // If all bookings for a date have been removed, delete the date from the bookingsSortedByDate object
      if (bookingsSortedByDate[date].length === 0) {
        delete bookingsSortedByDate[date];
      }
    }
    return bookingsSortedByDate;
  }
  updateTimeForAllDayBookingsUTC(bookings: CalendarEvent[]) {
    bookings.forEach((booking) => {
      if (booking.isAllDay) {
        booking.startTime = moment(booking.startTime)
          .utc()
          .format(DATETIMEFORMART);
        booking.endTime = moment(booking.endTime).utc().format(DATETIMEFORMART);
      }
    });
  }
}
